import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { useLongPress } from "@sukha/hooks";
const buttonVariants = cva("inline-flex justify-center items-center gap-2 aria-invalid:border-destructive disabled:opacity-50 focus-visible:border-ring rounded-md aria-invalid:ring-destructive/20 focus-visible:ring-ring/50 focus-visible:ring-[3px] dark:aria-invalid:ring-destructive/40 font-medium text-sm whitespace-nowrap transition-[color,box-shadow] cursor-pointer disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-hidden", {
  variants: {
    variant: {
      default: "bg-secondary-300/70 text-primary-foreground shadow-xs enabled:hover:bg-secondary-300/80 disabled:bg-primary-700",
      /** Teal brand primary — use for the main action when it should read as “primary” vs golden `default`. */
      primary: "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 focus-visible:ring-ring/50 disabled:opacity-50",
      destructive: "bg-destructive-strong text-white shadow-xs hover:bg-destructive-strong-hover focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 disabled:bg-destructive-700",
      /*
       * A TRUE outline: the surface shows straight through, and the edge is
       * the component.
       *
       * It was `border-input bg-background` — `oklch(0.205 0 0 / 0.7)` ringed
       * in `oklch(0.922 0 0)`, a near-black block with a bright ring, on top
       * of the translucent panel it was meant to belong to. 65 buttons.
       *
       * WCAG 1.4.11 asks 3:1 for a component boundary against EVERY adjacent
       * colour — the surface outside and the button's own fill inside. That
       * second half is why a translucent white fill had to go: `bg-white/5`
       * brightens alongside the border, so the inner edge never separates.
       * Measured across the three surfaces these sit on (glass panel, dialog,
       * page ground), worst of both adjacencies:
       *
       *   white/20 + bg-white/5     1.57   shipped first, failed 1.4.11
       *   white/50 + transparent    3.17   passes, no margin
       *   white/55 + transparent    3.46   PASSES, and is nothing like the
       *                                    8.57 of the ring it replaced
       *
       * Hover raises the edge with the fill so the state stays compliant:
       * worst boundary 3.38, worst label 4.97. Label contrast at rest is 7.33.
       */
      outline: "border border-white/55 bg-transparent text-primary-foreground shadow-xs hover:bg-secondary-300/20 hover:text-primary-foreground hover:border-white/70",
      secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 disabled:bg-secondary-700",
      ghost: "hover:bg-accent hover:text-accent-foreground hover:bg-primary/90",
      link: "text-primary underline-offset-4 hover:underline",
      circle: "bg-white/10 hover:bg-white/20 text-primary-foreground/60 hover:text-primary-foreground rounded-full"
    },
    size: {
      default: "h-9 px-4 py-2 has-[>svg]:px-3",
      sm: "h-8 rounded-md px-3 has-[>svg]:px-2.5",
      lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
      icon: "size-9",
      circle: "size-8 p-1"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
export interface ButtonProps extends Omit<React.ComponentProps<"button">, "onClick">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onLongClick?: (e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => void;
  longPressDelay?: number;
}
function Button(t0) {
  const $ = _c(28);
  let className;
  let onClick;
  let onLongClick;
  let props;
  let size;
  let t1;
  let t2;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      asChild: t1,
      onLongClick,
      longPressDelay: t2,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onClick;
    $[3] = onLongClick;
    $[4] = props;
    $[5] = size;
    $[6] = t1;
    $[7] = t2;
    $[8] = variant;
  } else {
    className = $[1];
    onClick = $[2];
    onLongClick = $[3];
    props = $[4];
    size = $[5];
    t1 = $[6];
    t2 = $[7];
    variant = $[8];
  }
  const asChild = t1 === undefined ? false : t1;
  const longPressDelay = t2 === undefined ? 500 : t2;
  const Comp = asChild ? Slot : "button";
  let t3;
  if ($[9] !== onLongClick) {
    t3 = e => {
      if (onLongClick) {
        onLongClick(e);
      }
    };
    $[9] = onLongClick;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const longPressCallback = t3;
  let t4;
  if ($[11] !== onClick) {
    t4 = e_0 => {
      if (onClick) {
        if ("touches" in e_0) {
          return;
        } else {
          onClick(e_0);
        }
      }
    };
    $[11] = onClick;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const clickCallback = t4;
  let t5;
  if ($[13] !== longPressDelay) {
    t5 = {
      delay: longPressDelay
    };
    $[13] = longPressDelay;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const {
    handlerProps
  } = useLongPress(longPressCallback, clickCallback, t5);
  let t6;
  if ($[15] !== handlerProps || $[16] !== onClick || $[17] !== onLongClick) {
    t6 = onLongClick ? handlerProps : onClick ? {
      onClick
    } : {};
    $[15] = handlerProps;
    $[16] = onClick;
    $[17] = onLongClick;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const eventHandlers = t6;
  let t7;
  if ($[19] !== className || $[20] !== size || $[21] !== variant) {
    t7 = cn(buttonVariants({
      variant,
      size,
      className
    }));
    $[19] = className;
    $[20] = size;
    $[21] = variant;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== Comp || $[24] !== eventHandlers || $[25] !== props || $[26] !== t7) {
    t8 = <Comp data-slot="button" className={t7} {...eventHandlers} {...props} />;
    $[23] = Comp;
    $[24] = eventHandlers;
    $[25] = props;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
}
export { Button, buttonVariants };